import React from 'react';
import { ViewType } from './Scheduler';
import { Doctor, Staff, Room, ScheduleItem, Appointment, Task, DentalProcedure, AdminTaskType } from './SchedulerTypes';
import { Tables } from '@/integrations/supabase/types';
import ErrorBoundary from './ErrorBoundary';
import DailyView from './DailyView';
import WeeklyView from './WeeklyView';
import MonthlyView from './MonthlyView';
import TableView from './TableView';
import AdminView from './AdminView';
import InventoryView from './InventoryView';
import PatientsView from './PatientsView';
import TasksView from './TasksView';
import AutomationView from './AutomationView';
import { useAuth } from '@/contexts/AuthContext';

interface SchedulerContentProps {
  currentView: ViewType;
  currentDate: Date;
  initialTab?: string;
  scheduleItems: ScheduleItem[];
  appointments: Appointment[];
  tasks: Task[];
  doctors: Doctor[];
  staff: Staff[];
  allDoctors: Doctor[];
  allStaff: Staff[];
  procedures: DentalProcedure[];
  adminTasks: AdminTaskType[];
  rooms: Room[];
  onAddScheduleItem: (item: Omit<ScheduleItem, 'id'>) => void;
  onUpdateScheduleItem: (id: string, updates: Partial<ScheduleItem>) => void;
  onDeleteScheduleItem: (id: string) => void;
  onDeleteScheduleSeries: (ids: string[]) => void;
  onAddAppointment: (appointment: Omit<Appointment, 'id' | 'createdAt' | 'updatedAt'>) => void;
  onUpdateAppointment: (id: string, updates: Partial<Appointment>) => void;
  onDeleteAppointment: (id: string) => void;
  onAddTask: (task: Omit<Task, 'id' | 'createdAt' | 'updatedAt'>) => void;
  onUpdateTask: (id: string, updates: Partial<Task>) => void;
  onDeleteTask: (id: string) => void;
  onUpdateDoctor: (id: string, updates: Partial<Doctor>) => void;
  onUpdateStaff: (id: string, updates: Partial<Staff>) => void;
  onUpdateProcedure: (id: string, updates: Partial<DentalProcedure>) => void;
  onUpdateAdminTask: (id: string, updates: Partial<AdminTaskType>) => void;
  onAddDoctor: (doctor: Omit<Doctor, 'id'>) => void;
  onAddStaff: (staff: Omit<Staff, 'id'>) => void;
  onAddProcedure: (procedure: Omit<DentalProcedure, 'id'>) => void;
  onAddAdminTask: (task: Omit<AdminTaskType, 'id'>) => void;
  getAppointmentForScheduleItem: (scheduleItemId: string) => Appointment | undefined;
  getTaskForScheduleItem: (scheduleItemId: string) => Task | undefined;
  onCopyScheduleItems: (date: string) => void;
  onPasteScheduleItems: (date: string) => void;
  copiedItems: ScheduleItem[];
  onDateChange: (date: Date) => void;
  onViewChange: (view: ViewType) => void;
  staffTodos: Tables<'staff_todos'>[];
  onAddStaffTodo: (todo: Omit<Tables<'staff_todos'>, 'id' | 'created_at' | 'updated_at'>) => void;
  onUpdateStaffTodo: (todo: Tables<'staff_todos'>) => void;
  onDeleteStaffTodo: (id: string) => void;
  // Lazy loading for monthly view
  loadFullDaySchedule?: (date: string) => Promise<ScheduleItem[]>;
  fullyLoadedDays?: Set<string>;
  dayScheduleCache?: Record<string, ScheduleItem[]>;
  loadingDays?: Set<string>;
  // Loading states
  isLoadingSchedules?: boolean;
  isFetchingSchedules?: boolean;
  // Refresh function for external triggers (e.g., Plato sync)
  onRefreshSchedule?: () => void;
}

const SchedulerContent = ({
  currentView,
  currentDate,
  initialTab,
  scheduleItems,
  appointments,
  tasks,
  doctors,
  staff,
  allDoctors,
  allStaff,
  procedures,
  adminTasks,
  rooms,
  onAddScheduleItem,
  onUpdateScheduleItem,
  onDeleteScheduleItem,
  onDeleteScheduleSeries,
  onAddAppointment,
  onUpdateAppointment,
  onDeleteAppointment,
  onAddTask,
  onUpdateTask,
  onDeleteTask,
  onUpdateDoctor,
  onUpdateStaff,
  onUpdateProcedure,
  onUpdateAdminTask,
  onAddDoctor,
  onAddStaff,
  onAddProcedure,
  onAddAdminTask,
  getAppointmentForScheduleItem,
  getTaskForScheduleItem,
  onCopyScheduleItems,
  onPasteScheduleItems,
  copiedItems,
  onDateChange,
  onViewChange,
  staffTodos,
  onAddStaffTodo,
  onUpdateStaffTodo,
  onDeleteStaffTodo,
  // Lazy loading for monthly view
  loadFullDaySchedule,
  fullyLoadedDays,
  dayScheduleCache,
  loadingDays,
  // Loading states
  isLoadingSchedules,
  isFetchingSchedules,
  // Refresh function for external triggers (e.g., Plato sync)
  onRefreshSchedule,
}: SchedulerContentProps) => {
  const { user, canAccessAdminPanel, canAccessPatients, canAccessInventory } = useAuth();

  // Check permissions for restricted views
  const canAccessTasks = () => {
    if (!user) return false;
    return ['admin', 'operations_manager', 'office_admin', 'office_user'].includes(user.role);
  };

  const canAccessAdmin = () => {
    return canAccessAdminPanel();
  };

  const canOpenPatients = () => {
    return canAccessPatients();
  };

  const canOpenInventory = () => {
    return canAccessInventory();
  };

  // Show unauthorized message if user tries to access restricted view
  if (currentView === 'admin' && !canAccessAdmin()) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="text-center">
          <h2 className="text-xl font-semibold text-gray-700 mb-2">Access Denied</h2>
          <p className="text-gray-500">You don't have permission to access the Admin view.</p>
        </div>
      </div>
    );
  }

  // Automation is admin-only: these reports summarise patient activity.
  if (currentView === 'automation' && !canAccessAdmin()) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="text-center">
          <h2 className="text-xl font-semibold text-gray-700 mb-2">Access Denied</h2>
          <p className="text-gray-500">You don't have permission to access the Automation view.</p>
        </div>
      </div>
    );
  }

  if (currentView === 'tasks' && !canAccessTasks()) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="text-center">
          <h2 className="text-xl font-semibold text-gray-700 mb-2">Access Denied</h2>
          <p className="text-gray-500">You don't have permission to access the Tasks view.</p>
        </div>
      </div>
    );
  }

  if (currentView === 'patients' && !canOpenPatients()) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="text-center">
          <h2 className="text-xl font-semibold text-gray-700 mb-2">Access Denied</h2>
          <p className="text-gray-500">You don't have permission to access the Patients view.</p>
        </div>
      </div>
    );
  }

  if (currentView === 'inventory' && !canOpenInventory()) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="text-center">
          <h2 className="text-xl font-semibold text-gray-700 mb-2">Access Denied</h2>
          <p className="text-gray-500">You don't have permission to access the Inventory view.</p>
        </div>
      </div>
    );
  }

  if (currentView === 'automation') {
    return (
      <ErrorBoundary>
        <AutomationView />
      </ErrorBoundary>
    );
  }

  if (currentView === 'admin') {
    return (
      <ErrorBoundary>
        <AdminView
          initialTab={initialTab}
          isLoadingSchedules={isLoadingSchedules}
          isFetchingSchedules={isFetchingSchedules}
        />
      </ErrorBoundary>
    );
  }

  if (currentView === 'patients') {
    return (
      <ErrorBoundary>
        <PatientsView initialTab={initialTab} />
      </ErrorBoundary>
    );
  }

  if (currentView === 'inventory') {
    return (
      <ErrorBoundary>
        <InventoryView />
      </ErrorBoundary>
    );
  }

  const commonProps = {
    currentDate,
    scheduleItems,
    appointments,
    tasks,
    doctors,
    staff,
    rooms,
    onAddScheduleItem,
    onUpdateScheduleItem,
    onDeleteScheduleItem,
    onDeleteScheduleSeries,
    onAddAppointment,
    onUpdateAppointment,
    onDeleteAppointment,
    onAddTask,
    onUpdateTask,
    onDeleteTask,
    getAppointmentForScheduleItem,
    getTaskForScheduleItem,
    onCopyScheduleItems,
    onPasteScheduleItems,
    copiedItems: copiedItems || [],
    copiedItemsCount: (copiedItems || []).length,
  };

  switch (currentView) {
    case 'daily':
      return (
        <ErrorBoundary>
          <DailyView
            {...commonProps}
            initialTab={initialTab}
            isLoadingSchedules={isLoadingSchedules}
            isFetchingSchedules={isFetchingSchedules}
            onRefreshSchedule={onRefreshSchedule}
          />
        </ErrorBoundary>
      );
    case 'weekly':
      return (
        <ErrorBoundary>
          <WeeklyView
            {...commonProps}
            initialTab={initialTab}
            onViewChange={onViewChange}
            onDateChange={onDateChange}
            isLoadingSchedules={isLoadingSchedules}
            isFetchingSchedules={isFetchingSchedules}
            onRefreshSchedule={onRefreshSchedule}
          />
        </ErrorBoundary>
      );
    case 'monthly':
      return (
        <ErrorBoundary>
          <MonthlyView
            currentDate={currentDate}
            scheduleItems={scheduleItems}
            doctors={doctors}
            staff={staff}
            rooms={rooms}
            initialTab={initialTab}
            onAddScheduleItem={onAddScheduleItem}
            onUpdateScheduleItem={onUpdateScheduleItem}
            onDateChange={onDateChange}
            onViewChange={onViewChange}
            onCopyScheduleItems={onCopyScheduleItems}
            onPasteScheduleItems={onPasteScheduleItems}
            copiedItems={copiedItems || []}
            onDeleteScheduleItem={onDeleteScheduleItem}
            onDeleteScheduleSeries={onDeleteScheduleSeries}
            isLoadingSchedules={isLoadingSchedules}
          />
        </ErrorBoundary>
      );
    case 'report':
      return (
        <ErrorBoundary>
          <TableView
            {...commonProps}
            initialTab={initialTab}
            isLoadingSchedules={isLoadingSchedules}
            isFetchingSchedules={isFetchingSchedules}
          />
        </ErrorBoundary>
      );
    case 'tasks':
      return (
        <ErrorBoundary>
          <TasksView
            staff={allStaff}
            staffTodos={staffTodos}
            onAddTodo={onAddStaffTodo}
            onUpdateTodo={onUpdateStaffTodo}
            onDeleteTodo={onDeleteStaffTodo}
            isLoadingSchedules={isLoadingSchedules}
            isFetchingSchedules={isFetchingSchedules}
          />
        </ErrorBoundary>
      );
    default:
      return (
        <ErrorBoundary>
          <TableView
            {...commonProps}
            initialTab={initialTab}
            isLoadingSchedules={isLoadingSchedules}
            isFetchingSchedules={isFetchingSchedules}
          />
        </ErrorBoundary>
      );
  }
};

export default SchedulerContent;
